import { Link, type MetaFunction } from "react-router";

import { LegalDocument } from "@/legal/document";

const PASSPORT_GUIDANCE_URL =
  "https://travel.state.gov/content/travel/en/passports/how-apply/photos.html";

export const meta: MetaFunction = () => [
  { title: `Terms of Use | ${publicEnv.siteTitle}` },
];

// Set expectations for a free browser tool whose output must still be checked
// against current government photo rules.
//
export default function Terms() {
  return (
    <LegalDocument
      description="The terms for using the privacy.photos browser tool to crop and arrange photos for printing."
      title="Terms of Use"
      updated="August 11, 2026"
    >
      <section>
        <h2>1. Agreement and operator</h2>
        <p>
          These Terms govern your use of privacy.photos (the
          &ldquo;Service&rdquo;), operated by {publicEnv.siteLegalEntity}
          (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By using
          the Service, you agree to these Terms and our{" "}
          <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not
          use the Service.
        </p>
      </section>

      <section>
        <h2>2. Eligibility</h2>
        <p>
          You must be legally permitted to use the Service. If you have not
          reached the age of legal majority where you live, a parent or guardian
          must supervise your use and review these Terms for you. Do not select
          another person&apos;s photo unless you have their permission or
          another lawful basis to process it on your device.
        </p>
      </section>

      <section>
        <h2>3. Local photo processing</h2>
        <p>
          The Service reads selected image files into browser memory, can
          convert supported formats, downloads machine-learning model files,
          performs background and face analysis locally, lets you crop and
          position photos, and creates a printable image for download. Your
          selected photos and generated output are not uploaded to our server.
        </p>
        <p>
          You remain responsible for the photos and output on your device,
          including clearing them from browser memory, downloads, backups,
          shared devices, and print services you choose to use.
        </p>
      </section>

      <section>
        <h2>4. Government requirements and no guarantee</h2>
        <p>
          privacy.photos is an independent convenience tool. It is not a
          government service, is not affiliated with or endorsed by the U.S.
          Department of State or another issuing authority, and cannot approve a
          passport or identity photo.
        </p>
        <p>
          Requirements can change and vary by document, submission method, and
          issuing authority. The U.S. Department of State currently warns that
          passport photos should not be digitally altered or changed with
          artificial intelligence. Background removal, automatic positioning,
          cropping, format conversion, and other Service features may make a
          photo unacceptable even when its dimensions appear correct.
        </p>
        <p>
          Review the issuing authority&apos;s current rules before using an
          output. For a U.S. passport, consult the{" "}
          <a href={PASSPORT_GUIDANCE_URL} rel="noreferrer" target="_blank">
            official photo requirements
          </a>
          . Acceptance is determined solely by the relevant authority, and we do
          not guarantee that any output will be accepted.
        </p>
      </section>

      <section>
        <h2>5. Your photos and output</h2>
        <p>
          We do not receive or claim ownership of photos you process locally or
          output you generate. You are responsible for having the rights and
          permissions needed to use, edit, print, and submit them. Do not use
          the Service to falsify an identity document, misrepresent a person, or
          violate privacy, publicity, intellectual-property, or other rights.
        </p>
      </section>

      <section>
        <h2>6. Third-party services</h2>
        <p>
          Your browser may download open machine-learning model files from
          Hugging Face. If you follow a voluntary support link, Stripe or the
          linked payment page handles that visit and payment under its own
          terms. Links may also lead to government guidance or other services we
          do not control. We are not responsible for third-party content,
          availability, security, or data practices.
        </p>
      </section>

      <section>
        <h2>7. Voluntary support</h2>
        <p>
          A contribution through an external support link is voluntary. It does
          not purchase a subscription, unlock features, guarantee availability,
          or create a right to a particular result. The payment page shows the
          amount and any available refund terms before you pay. Contact us if a
          contribution was made in error.
        </p>
      </section>

      <section>
        <h2>8. Acceptable use</h2>
        <p>You may not:</p>
        <ul>
          <li>use the Service for unlawful, deceptive, or harmful activity;</li>
          <li>
            process a photo or biometric likeness without the rights or
            permission required by applicable law;
          </li>
          <li>
            probe, disrupt, overload, or bypass security or access controls;
          </li>
          <li>
            introduce malware or interfere with another person&apos;s use; or
          </li>
          <li>
            falsely claim that an output was approved by us or a government
            authority.
          </li>
        </ul>
      </section>

      <section>
        <h2>9. Our intellectual property</h2>
        <p>
          The Service, software, design, branding, and original materials belong
          to us or our licensors. Subject to these Terms, we grant you a
          limited, revocable, non-transferable right to use the Service for its
          intended purpose. Third-party software and model files remain subject
          to their respective licenses. If you provide feedback, we may use it
          without restriction or payment to you.
        </p>
      </section>

      <section>
        <h2>10. Availability and changes</h2>
        <p>
          We may improve, limit, replace, or discontinue the Service or a
          feature. The Service may be unavailable because of maintenance,
          failures, provider changes, or events beyond our control. Keep your
          original photos and verify downloaded output before relying on it.
        </p>
      </section>

      <section>
        <h2>11. Disclaimers</h2>
        <p>
          TO THE EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED &ldquo;AS
          IS&rdquo; AND &ldquo;AS AVAILABLE.&rdquo; WE DISCLAIM IMPLIED
          WARRANTIES, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
          PURPOSE, TITLE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT
          PROCESSING WILL BE ERROR-FREE, THAT AN OUTPUT WILL MATCH PRINT
          DIMENSIONS ON A PARTICULAR DEVICE OR PRINTER, OR THAT AN AUTHORITY
          WILL ACCEPT IT.
        </p>
        <p>
          Nothing in these Terms excludes a warranty or consumer right that
          cannot lawfully be excluded.
        </p>
      </section>

      <section>
        <h2>12. Limitation of liability</h2>
        <p>
          TO THE EXTENT PERMITTED BY LAW, NEITHER WE NOR OUR SUPPLIERS WILL BE
          LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
          PUNITIVE DAMAGES, OR FOR LOST FILES, APPLICATION DELAYS, REJECTED
          PHOTOS, REPRINTING COSTS, LOST PROFITS, OR BUSINESS INTERRUPTION. OUR
          TOTAL LIABILITY ARISING OUT OF THE SERVICE OR THESE TERMS WILL NOT
          EXCEED $100. THESE LIMITS APPLY TO THE FULLEST EXTENT PERMITTED BY
          LAW.
        </p>
      </section>

      <section>
        <h2>13. Governing law and disputes</h2>
        <p>
          Washington law governs these Terms, without regard to conflict-of-law
          rules. Before filing a claim, contact us and allow 30 days for an
          informal resolution. If that does not resolve the dispute, the state
          and federal courts with jurisdiction in King County, Washington will
          have exclusive jurisdiction, except that an eligible individual claim
          may be brought in small claims court.
        </p>
        <p>
          Mandatory consumer protections in your home jurisdiction continue to
          apply where they cannot be waived.
        </p>
      </section>

      <section>
        <h2>14. Changes and general terms</h2>
        <p>
          We may update these Terms. We will post the updated date and provide
          reasonable notice before a material change takes effect. Continued use
          after the effective date means you accept the updated Terms.
        </p>
        <p>
          These Terms and the policies they incorporate are the entire agreement
          about the Service. If one provision is unenforceable, the remaining
          provisions continue. A failure to enforce a provision is not a waiver.
        </p>
      </section>

      <section>
        <h2>15. Contact</h2>
        <p>
          Questions about these Terms may be sent to{" "}
          <a href={`mailto:${publicEnv.siteSupportEmail}`}>
            {publicEnv.siteSupportEmail}
          </a>
          .
        </p>
      </section>
    </LegalDocument>
  );
}
